// src/firebase/injuriesService.js
//
// Sincronizza gli infortuni Serie A da FantaMaster e li applica come categoria
// "injury" + nota sui giocatori delle aste dell'utente.
//
// Fonte: https://apicdn.fantamaster.it/lineups/ (stesso CDN e stesso endpoint
// già usato in playerService.js per le probabili formazioni), campi
// "home_injured"/"away_injured" di ogni partita — non l'endpoint dedicato
// /injuries/, che si è rivelato abbandonato (dati fermi a stagioni passate,
// verificato il 2026-09-23: nessuna squadra/infortunio corrispondeva più alla
// stagione in corso). /lineups/ è invece aggiornato quotidianamente perché
// serve anche le probabili formazioni, quindi eredita la stessa freschezza.
// Qui NON viene riusata la cache client-side 3h di playerService.js: questo
// fetch è indipendente (stesso URL, chiamata separata) e resta protetto dal
// suo lock Firestore — leggera duplicazione di rete accettata per non
// intrecciare due meccanismi di cache con scopi diversi (uno effimero per
// tab, uno persistito e condiviso tra tutti gli utenti).
//
// Il fetch verso l'API esterna è protetto da un lock su Firestore (appMeta/injuriesSync):
// il primo client che nota che è passato più di MIN_SYNC_INTERVAL_MS prova a fare il
// fetch, tutti gli altri client leggono solo Firestore. Questo garantisce al massimo
// ~5 chiamate reali al giorno complessivamente, indipendentemente da quanti utenti
// abbiano l'app aperta. Nessuna API key richiesta: l'endpoint è pubblico.
import {
  collection,
  doc,
  getDocs,
  runTransaction,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { saveAuctionPlayerCategory, saveAuctionPlayerNote } from './auctionService';

const INJURIES_COLLECTION = 'playerInjuries';
const SYNC_META_DOC = ['appMeta', 'injuriesSync'];

// 24h / 5 = 4h48m: garantisce al massimo ~5 fetch reali al giorno anche con più client attivi.
const MIN_SYNC_INTERVAL_MS = (24 / 5) * 60 * 60 * 1000;

const FANTAMASTER_LINEUPS_URL = 'https://apicdn.fantamaster.it/lineups/';

// Esportato: chi mostra le note dei giocatori (es. AuctionUnavailableTab) lo usa
// per distinguere a colpo d'occhio una nota scritta da questa funzione da una
// scritta a mano dall'utente, senza duplicare la stringa altrove.
export const AUTO_NOTE_PREFIX = '🩹 Infortunio (auto)';

// Difensivo: i campi home_injured/away_injured dovrebbero contenere solo
// infortuni veri e propri, ma per sicurezza scartiamo eventuali voci che
// parlano di squalifiche/cartellini.
const NON_INJURY_REASON_PATTERN = /squalific|cartellin|diffid/i;

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

// Stessa normalizzazione usata per indicizzare la mappa infortuni: esportata
// perché chi confronta i propri giocatori con `getCurrentInjuriesByName()`
// deve normalizzare i nomi esattamente allo stesso modo.
export function normalizeInjuryKey(value = '') {
  return normalizeText(value);
}

function formatDateShort(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

const ITALIAN_MONTHS = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];
const RETURN_DATE_PATTERN = new RegExp(
  `(?:(inizio|met[aà]|fine)\\s+(?:di\\s+)?)?\\b(${ITALIAN_MONTHS.join('|')})\\b`,
  'gi'
);

// Il "desc" di /lineups/ è una frase discorsiva, non un campo data strutturato
// come il vecchio /injuries/ (che aveva "back"): il testo spesso cita PIÙ di
// un mese, e non tutti sono il rientro — vanno scartati quelli che sono in
// realtà la data dell'infortunio/dell'operazione:
//  - "il 20 settembre", "operato il 3 settembre" → data specifica (giorno +
//    mese) di QUANDO è successo qualcosa, mai una stima di rientro (quella è
//    sempre e solo il mese, es. "atteso da ottobre", "non prima di dicembre");
//  - "fermo da/fermato a metà settembre" → da QUANDO è fermo (esordio dello
//    stop), stesso identico problema ma senza il numero del giorno a tradirlo.
// Scartati questi due pattern, si prende l'ultimo mese rimasto nel testo — è
// quasi sempre quello del rientro, perché la frase segue tipicamente l'ordine
// "cos'è successo (con la data) → quando torna". Se non resta nessun mese
// valido (es. "la sua presenza resta in forte dubbio", senza nessuna stima),
// null: nessun rientro stimabile, non un errore da segnalare.
// Validato su un campione reale di 48 infortuni (2026-09-23): ~80% di
// estrazioni corrette, zero falsi positivi residui sul campione.
const DAY_BEFORE_MONTH_PATTERN = /\d{1,2}\s*$/;
const ONSET_PHRASE_PATTERN = /ferm[ao](?:to|ta)?\s+(?:da|a)\s*$/i;

function extractExpectedReturn(desc) {
  const text = String(desc || '');
  const matches = [...text.matchAll(RETURN_DATE_PATTERN)];

  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const before = text.slice(Math.max(0, match.index - 25), match.index);
    if (DAY_BEFORE_MONTH_PATTERN.test(before) || ONSET_PHRASE_PATTERN.test(before)) continue;

    const [, qualifier, month] = match;
    const normalizedQualifier = qualifier ? qualifier.toLowerCase().replace('à', 'a') : '';
    return normalizedQualifier ? `${normalizedQualifier} ${month.toLowerCase()}` : month.toLowerCase();
  }

  return null;
}

async function fetchSerieAInjuriesFromFantaMaster() {
  const res = await fetch(FANTAMASTER_LINEUPS_URL);

  if (!res.ok) {
    throw new Error(`FantaMaster lineups: HTTP ${res.status}`);
  }

  const json = await res.json();
  const matches = Array.isArray(json?.lineups) ? json.lineups : [];
  const rows = [];

  // Niente campo "severity" strutturato in questa fonte (a differenza del
  // vecchio /injuries/): il "desc" è una frase discorsiva che include motivo
  // e rientro insieme — expectedReturn viene estratto da lì (vedi sopra),
  // reason resta il testo completo per la nota.
  matches.forEach((match) => {
    [
      ['home_injured', match?.home],
      ['away_injured', match?.away],
    ].forEach(([field, team]) => {
      (match?.[field] || []).forEach((entry) => {
        const playerName = String(entry?.player || '').trim();
        const reason = String(entry?.desc || '').trim();
        if (!playerName || !reason || NON_INJURY_REASON_PATTERN.test(reason)) return;
        rows.push({
          playerName,
          team: String(team || '').trim(),
          reason,
          severity: null,
          expectedReturn: extractExpectedReturn(reason),
        });
      });
    });
  });

  return rows;
}

async function saveInjuriesToFirestore(injuries) {
  const colRef = collection(db, INJURIES_COLLECTION);
  const existingSnap = await getDocs(colRef);
  const existingById = new Map(existingSnap.docs.map((d) => [d.id, d.data() || {}]));

  const nextIds = new Set();
  const batch = writeBatch(db);
  const now = Date.now();

  injuries.forEach((injury) => {
    const key = normalizeText(injury.playerName);
    if (!key) return;
    nextIds.add(key);

    const previous = existingById.get(key);
    const sameReason = previous && String(previous.reason || '') === injury.reason;
    const firstDetectedAt = sameReason ? previous.firstDetectedAt ?? now : now;

    batch.set(doc(colRef, key), {
      playerName: injury.playerName,
      team: injury.team,
      reason: injury.reason,
      severity: injury.severity,
      expectedReturn: injury.expectedReturn,
      firstDetectedAt,
      updatedAt: now,
    });
  });

  // Rimuove chi non è più nella lista (guarito).
  existingById.forEach((_data, id) => {
    if (!nextIds.has(id)) batch.delete(doc(colRef, id));
  });

  await batch.commit();
}

/**
 * Da chiamare all'avvio/apertura di un'asta. Se è passato più di ~4h48m dall'ultimo
 * tentativo, prova a fare il fetch da FantaMaster e a salvare su Firestore. Se un
 * altro client ha già "prenotato" lo slot, esce subito senza fare rete.
 * Non lancia mai eccezioni: eventuali errori vengono solo loggati.
 */
export async function ensureInjuriesSynced() {
  const syncRef = doc(db, ...SYNC_META_DOC);

  let claimed = false;
  try {
    claimed = await runTransaction(db, async (tx) => {
      const snap = await tx.get(syncRef);
      const data = snap.exists() ? snap.data() : null;
      const lastAttemptAt = Number(data?.lastAttemptAt || 0);

      if (Date.now() - lastAttemptAt < MIN_SYNC_INTERVAL_MS) {
        return false;
      }

      tx.set(syncRef, { lastAttemptAt: Date.now() }, { merge: true });
      return true;
    });
  } catch (err) {
    console.warn('[injuriesService] lock transaction failed', err);
    return;
  }

  if (!claimed) return;

  try {
    const injuries = await fetchSerieAInjuriesFromFantaMaster();
    await saveInjuriesToFirestore(injuries);
    await runTransaction(db, async (tx) => {
      tx.set(
        syncRef,
        { lastSuccessAt: Date.now(), lastError: null, count: injuries.length, updatedAt: serverTimestamp() },
        { merge: true }
      );
    });
  } catch (err) {
    console.warn('[injuriesService] sync failed', err);
    try {
      await runTransaction(db, async (tx) => {
        tx.set(syncRef, { lastError: String(err?.message || err) }, { merge: true });
      });
    } catch {
      // ignora: il lock resta comunque scritto, ritenteremo al prossimo slot
    }
  }
}

/**
 * Legge la lista corrente degli infortunati da Firestore (senza mai chiamare l'API
 * esterna) indicizzata per nome giocatore normalizzato.
 */
export async function getCurrentInjuriesByName() {
  const snap = await getDocs(collection(db, INJURIES_COLLECTION));
  const map = new Map();
  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const key = normalizeText(data.playerName);
    if (key) map.set(key, data);
  });
  return map;
}

function buildAutoNoteText(injury) {
  const reason = injury.reason || 'infortunato';
  const parts = [`${AUTO_NOTE_PREFIX}: ${reason}`];

  if (Number.isFinite(injury.severity)) {
    parts.push(`gravità ${injury.severity}/10`);
  }

  if (injury.expectedReturn) {
    parts.push(`rientro previsto: ${injury.expectedReturn}`);
  } else {
    const since = formatDateShort(injury.firstDetectedAt);
    if (since) parts.push(`rilevato dal ${since}`);
  }

  return parts.join(' — ');
}

/**
 * Applica automaticamente la categoria "injury" (+ una nota con motivo/gravità/rientro)
 * ai giocatori di una specifica asta che risultano infortunati, e la rimuove quando
 * il giocatore non è più nella lista infortunati (rientro).
 *
 * Non tocca mai una categoria diversa da "none" già impostata manualmente, e non
 * sovrascrive/rimuove mai una nota che non sia stata scritta da questa stessa funzione
 * (riconosciuta dal prefisso "🩹 Infortunio (auto)"), per non perdere note personali.
 * Allo stesso modo, la rimozione della categoria "injury" al rientro avviene solo se
 * la nota presente è ancora quella automatica: è il solo segnale disponibile per
 * distinguere un tag automatico da uno impostato a mano dall'utente.
 *
 * Ritorna solo le voci effettivamente aggiornate, da unire allo stato locale.
 */
export async function applyInjuryTagsToAuction({
  uid,
  auctionId,
  players = [],
  existingCategories = {},
  existingNotes = {},
}) {
  if (!uid || !auctionId || !players.length) {
    return { updatedCategories: {}, updatedNotes: {} };
  }

  let injuriesByName;
  try {
    injuriesByName = await getCurrentInjuriesByName();
  } catch (err) {
    console.warn('[injuriesService] impossibile leggere gli infortuni', err);
    return { updatedCategories: {}, updatedNotes: {} };
  }

  const updatedCategories = {};
  const updatedNotes = {};

  for (const player of players) {
    const key = normalizeText(player?.name);
    const injury = key ? injuriesByName.get(key) : null;

    const playerId = String(player.id);
    const currentCategory = String(existingCategories?.[playerId] || 'none');
    const currentNote = String(existingNotes?.[playerId] || '');
    const noteIsAutoTag = currentNote.startsWith(AUTO_NOTE_PREFIX);

    if (!injury) {
      // Rientro: rimuove categoria e nota solo se erano state impostate da questa
      // stessa funzione, per non toccare tag/note messi a mano dall'utente.
      if (currentCategory === 'injury' && noteIsAutoTag) {
        try {
          await saveAuctionPlayerCategory(uid, auctionId, playerId, 'none');
          updatedCategories[playerId] = 'none';
        } catch (err) {
          console.warn('[injuriesService] impossibile rimuovere categoria infortunio', err);
        }
        try {
          await saveAuctionPlayerNote(uid, auctionId, playerId, '');
          updatedNotes[playerId] = '';
        } catch (err) {
          console.warn('[injuriesService] impossibile rimuovere la nota infortunio', err);
        }
      }
      continue;
    }

    if (currentCategory === 'none') {
      try {
        await saveAuctionPlayerCategory(uid, auctionId, playerId, 'injury');
        updatedCategories[playerId] = 'injury';
      } catch (err) {
        console.warn('[injuriesService] impossibile impostare categoria infortunio', err);
      }
    }

    const noteIsOurs = !currentNote || noteIsAutoTag;
    const nextNote = buildAutoNoteText(injury);

    if (noteIsOurs && currentNote !== nextNote) {
      try {
        await saveAuctionPlayerNote(uid, auctionId, playerId, nextNote);
        updatedNotes[playerId] = nextNote;
      } catch (err) {
        console.warn('[injuriesService] impossibile scrivere la nota infortunio', err);
      }
    }
  }

  return { updatedCategories, updatedNotes };
}
